/**
 * Rhino 脚本注入常量定义
 * 对应 Android 端的注入对象
 */

import { loadI18nValue } from '@fanfanlo';

/**
 * WebView JS 注入对象
 * 来自: com.fanfanlo.lib.components.webview.WebViewJsInjectKeys
 */
const WebViewJsInjectKeysConst = {
    Window: "Window",
    WindowUtils: "WindowUtils",
    webViewJs: "webViewJs",
    console: "console",
    UIThreadUtil: "UIThreadUtil",
} as const;

/**
 * Rhino 脚本注入对象
 * 来自: com.fanfanlo.droidlib.auto.rhino.InjectNames
 */
const RhinoInjectNamesConst = {
    dispatcher: "dispatcher",
    quicker: "quicker",
    javaRunner: "javaRunner",
    proxy: "proxy",
    a11yProxy: "a11yProxy",
    a11yModel: "a11yModel",
    WebViewJsDispatcher: "WebViewJsDispatcher",
} as const;

/**
 * 所有可用的注入对象键名
 */
export const AllInjectKeys = {
    ...WebViewJsInjectKeysConst,
    ...RhinoInjectNamesConst,
} as const;

/**
 * 注入对象键名的联合类型
 */
export type InjectKey = keyof typeof AllInjectKeys;

/**
 * 注入对象值的联合类型
 */
export type InjectKeyValue = (typeof AllInjectKeys)[InjectKey];

/**
 * 向后兼容: 导出 WebViewJsInjectKeys (原enum改为const对象)
 * 使用方式不变: WebViewJsInjectKeys.Window
 */
export const WebViewJsInjectKeys = WebViewJsInjectKeysConst;

/**
 * 向后兼容: 导出 RhinoInjectNames (原enum改为const对象)
 * 使用方式不变: RhinoInjectNames.dispatcher
 */
export const RhinoInjectNames = RhinoInjectNamesConst;

/**
 * 向后兼容: 原有类型别名
 */
export type InjectKeyType = InjectKey;

/**
 * 注入键选项数组 - 用于 select 等 UI 组件
 * 包含分组、键名、值和 i18n key
 */
export const InjectKeyOptions = [
    ...Object.keys(WebViewJsInjectKeysConst).map(key => ({
        group: 'WebViewJsInjectKeys' as const,
        key: key as keyof typeof WebViewJsInjectKeysConst,
        value: WebViewJsInjectKeysConst[key as keyof typeof WebViewJsInjectKeysConst],
        i18nKey: `WebViewJsInjectKeys.${key}`,
    })),
    ...Object.keys(RhinoInjectNamesConst).map(key => ({
        group: 'RhinoInjectNames' as const,
        key: key as keyof typeof RhinoInjectNamesConst,
        value: RhinoInjectNamesConst[key as keyof typeof RhinoInjectNamesConst],
        i18nKey: `RhinoInjectNames.${key}`,
    })),
] as const;

/**
 * 获取所有注入键名的数组
 */
export function getAllInjectKeys(): string[] {
    return Object.keys(AllInjectKeys);
}

/**
 * 检查是否是有效的注入键名
 */
export function isValidInjectKey(key: string): boolean {
    return getAllInjectKeys().includes(key);
}

/**
 * 获取注入键的国际化描述
 * @param i18nKey i18n键名,格式为 "WebViewJsInjectKeys.Window" 或 "RhinoInjectNames.dispatcher"
 * @returns 返回对应的国际化描述文本
 * @example
 * const desc = await getInjectKeyDescription('WebViewJsInjectKeys.Window');
 * // 返回: "全局Window,可以派发window的退出事件"
 */
export async function getInjectKeyDescription(i18nKey: string): Promise<string> {
    console.log(`getInjectKeyDescription: i18nKey="${i18nKey}"`);
    const namespace = 'app-static/project/content';
    console.log(`getInjectKeyDescription: namespace="${namespace}"`);
    const fullI18nKey = `RhinoInjectKeys.${i18nKey}`;
    console.log(`getInjectKeyDescription: fullI18nKey="${fullI18nKey}"`);
    const description = await loadI18nValue(namespace, fullI18nKey);
    console.log(`getInjectKeyDescription: description="${description}"`);
    return description;
}

/**
 * 根据 InjectKey 获取对应的国际化描述
 * @param key 注入键名,如 "Window", "dispatcher"
 * @returns 返回对应的国际化描述文本
 * @example
 * const desc = await getInjectKeyDescriptionByKey('Window');
 * // 返回: "全局Window,可以派发window的退出事件"
 */
export async function getInjectKeyDescriptionByKey(key: InjectKey): Promise<string> {
    console.log(`getInjectKeyDescriptionByKey: key="${key}"`);
    const option = InjectKeyOptions.find(opt => opt.key === key);
    console.log(`getInjectKeyDescriptionByKey: option=${JSON.stringify(option)}`);
    if (!option) {
        console.error(`getInjectKeyDescriptionByKey error: key="${key}" not found in InjectKeyOptions`);
        return '';
    }
    const description = await getInjectKeyDescription(option.i18nKey);
    console.log(`getInjectKeyDescriptionByKey: description="${description}"`);
    return description;
}
